// Panneau des propriétés du champ sélectionné dans le constructeur : libellé et code, aide,
// obligation, valeur par défaut, règles de validation selon le type, options, colonnes, type de
// pièce, condition d'affichage, calcul. Toute modification est répercutée immédiatement.
import { LIBELLES_TYPES_CHAMP, TYPES_NUMERIQUES, type Champ, type ValidationChamp } from '@cc/contrats';
import { Bouton, CaseACocher, Champ as ChampTexte, ConfirmationIntegree, DateChamp, Selection, ZoneTexte } from '@cc/ui';
import type { TypePiece } from '../contenu';
import { EditeurCondition } from './EditeurCondition';
import { EditeurCalcul, EditeurColonnes, EditeurOptions } from './EditeurOptionsColonnes';
import demarches from '../demarches.module.css';
import styles from './constructeur.module.css';

export interface ProprietesEditeurChamp {
  champ: Champ;
  onChanger: (champ: Champ) => void;
  onSupprimer: () => void;
  /** Tous les champs du formulaire (conditions et calculs). */
  champs: Champ[];
  typesPieces: TypePiece[];
  rubriqueTarif: string | null;
  codesPris: string[];
}

const AVEC_LONGUEUR = ['texte_court', 'texte_long'];
const AVEC_BORNES = ['nombre', 'montant', 'choix_multiple', 'tableau'];

function nombreOuAbsent(brut: string): number | undefined {
  if (brut.trim() === '') return undefined;
  const n = Number(brut.replace(',', '.'));
  return Number.isNaN(n) ? undefined : n;
}

function ValeurParDefaut({ champ, onChanger }: { champ: Champ; onChanger: (v: unknown) => void }) {
  const v = champ.valeur_defaut;
  if (champ.type === 'oui_non') {
    return <Selection libelle="Valeur par défaut" value={v === true ? 'oui' : v === false ? 'non' : ''} optionVide="Aucune" options={[{ valeur: 'oui', libelle: 'Oui' }, { valeur: 'non', libelle: 'Non' }]} onChange={(e) => onChanger(e.target.value === '' ? undefined : e.target.value === 'oui')} />;
  }
  if (champ.type === 'choix_unique') {
    return <Selection libelle="Valeur par défaut" value={typeof v === 'string' ? v : ''} optionVide="Aucune" options={(champ.options ?? []).map((o) => ({ valeur: o.valeur, libelle: o.libelle }))} onChange={(e) => onChanger(e.target.value || undefined)} />;
  }
  if (champ.type === 'nombre' || champ.type === 'montant') {
    return <ChampTexte libelle="Valeur par défaut" type="number" step={champ.type === 'montant' ? 1 : 'any'} value={typeof v === 'number' ? String(v) : ''} facultatif onChange={(e) => onChanger(nombreOuAbsent(e.target.value))} />;
  }
  if (champ.type === 'texte_court' || champ.type === 'telephone' || champ.type === 'courriel') {
    return <ChampTexte libelle="Valeur par défaut" value={typeof v === 'string' ? v : ''} facultatif onChange={(e) => onChanger(e.target.value || undefined)} />;
  }
  if (champ.type === 'date') {
    return <DateChamp libelle="Valeur par défaut" value={typeof v === 'string' ? v : ''} facultatif onChange={(e) => onChanger(e.target.value || undefined)} />;
  }
  return null;
}

export function EditeurChamp({ champ, onChanger, onSupprimer, champs, typesPieces, rubriqueTarif, codesPris }: ProprietesEditeurChamp) {
  const maj = (patch: Partial<Champ>) => onChanger({ ...champ, ...patch });
  const majValidation = (patch: Partial<ValidationChamp>) => {
    const v: Record<string, unknown> = { ...(champ.validation ?? {}), ...patch };
    for (const cle of Object.keys(v)) if (v[cle] === undefined || v[cle] === '') delete v[cle];
    maj({ validation: Object.keys(v).length > 0 ? (v as ValidationChamp) : undefined });
  };
  const val = champ.validation ?? {};
  const autres = champs.filter((c) => c.code !== champ.code);
  const codeEnDouble = codesPris.filter((c) => c === champ.code).length > 1;
  const codeInvalide = !/^[a-z][a-z0-9_]{0,62}$/.test(champ.code);

  return (
    <div className={styles.proprietes} aria-live="polite">
      <p className="cc-etiquette">Champ sélectionné, {LIBELLES_TYPES_CHAMP[champ.type]}</p>
      <ChampTexte libelle="Libellé" value={champ.libelle} required onChange={(e) => maj({ libelle: e.target.value })} />
      <ChampTexte
        libelle="Code technique"
        value={champ.code}
        required
        className="cc-mono"
        aide="Minuscules, chiffres et soulignés ; il identifie la donnée dans les dossiers et ne change plus après publication."
        erreur={codeInvalide ? 'Code invalide' : codeEnDouble ? 'Ce code est déjà utilisé' : undefined}
        onChange={(e) => maj({ code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
      />
      <ZoneTexte libelle="Aide affichée à l'usager" value={champ.aide ?? ''} facultatif rows={2} onChange={(e) => maj({ aide: e.target.value || undefined })} />
      {!champ.calcul ? <CaseACocher libelle="Obligatoire" checked={champ.obligatoire} onChange={(e) => maj({ obligatoire: e.target.checked })} /> : null}
      {!champ.calcul ? <ValeurParDefaut champ={champ} onChanger={(v) => maj({ valeur_defaut: v })} /> : null}

      {(champ.type === 'choix_unique' || champ.type === 'choix_multiple') && <EditeurOptions options={champ.options ?? []} onChanger={(options) => maj({ options })} />}
      {champ.type === 'tableau' ? <EditeurColonnes colonnes={champ.colonnes ?? []} onChanger={(colonnes) => maj({ colonnes })} /> : null}
      {champ.type === 'fichier' ? (
        <Selection
          libelle="Type de pièce attendu"
          value={champ.type_piece ?? ''}
          optionVide="Tout type de pièce"
          options={typesPieces.map((t) => ({ valeur: t.code, libelle: `${t.libelle}${t.local ? ' (local)' : ''}` }))}
          onChange={(e) => maj({ type_piece: e.target.value || undefined })}
        />
      ) : null}

      <fieldset className={demarches.sousEditeur}>
        <legend>Validation</legend>
        {AVEC_LONGUEUR.includes(champ.type) ? (
          <div className={demarches.deuxChamps}>
            <ChampTexte libelle="Longueur minimale" type="number" min={0} value={val.longueur_min ?? ''} facultatif onChange={(e) => majValidation({ longueur_min: nombreOuAbsent(e.target.value) })} />
            <ChampTexte libelle="Longueur maximale" type="number" min={1} value={val.longueur_max ?? ''} facultatif onChange={(e) => majValidation({ longueur_max: nombreOuAbsent(e.target.value) })} />
          </div>
        ) : null}
        {AVEC_BORNES.includes(champ.type) ? (
          <div className={demarches.deuxChamps}>
            <ChampTexte libelle={champ.type === 'choix_multiple' ? 'Choix minimum' : champ.type === 'tableau' ? 'Lignes minimum' : 'Minimum'} type="number" step="any" value={val.min ?? ''} facultatif onChange={(e) => majValidation({ min: nombreOuAbsent(e.target.value) })} />
            <ChampTexte libelle={champ.type === 'choix_multiple' ? 'Choix maximum' : champ.type === 'tableau' ? 'Lignes maximum' : 'Maximum'} type="number" step="any" value={val.max ?? ''} facultatif onChange={(e) => majValidation({ max: nombreOuAbsent(e.target.value) })} />
          </div>
        ) : null}
        {champ.type === 'date' ? (
          <div className={demarches.deuxChamps}>
            <DateChamp libelle="Date au plus tôt" value={val.date_min ?? ''} facultatif onChange={(e) => majValidation({ date_min: e.target.value || undefined })} />
            <DateChamp libelle="Date au plus tard" value={val.date_max ?? ''} facultatif onChange={(e) => majValidation({ date_max: e.target.value || undefined })} />
          </div>
        ) : null}
        {champ.type === 'texte_court' ? (
          <>
            <ChampTexte libelle="Motif (expression régulière)" className="cc-mono" value={val.motif ?? ''} facultatif aide="La valeur doit respecter le motif en entier, par exemple [0-9]{9} pour neuf chiffres." onChange={(e) => majValidation({ motif: e.target.value || undefined })} />
            {val.motif ? <ChampTexte libelle="Message si le motif n'est pas respecté" value={val.message_motif ?? ''} facultatif onChange={(e) => majValidation({ message_motif: e.target.value || undefined })} /> : null}
          </>
        ) : null}
        {!AVEC_LONGUEUR.includes(champ.type) && !AVEC_BORNES.includes(champ.type) && champ.type !== 'date' ? <p className={demarches.aideDiscrete}>Aucune règle supplémentaire pour ce type : le format est contrôlé par le moteur.</p> : null}
      </fieldset>

      <EditeurCondition condition={champ.visible_si} onChanger={(visible_si) => maj({ visible_si })} champs={autres} />
      {TYPES_NUMERIQUES.includes(champ.type) ? <EditeurCalcul champ={champ} calcul={champ.calcul} onChanger={(calcul) => maj({ calcul, obligatoire: calcul ? false : champ.obligatoire, valeur_defaut: calcul ? undefined : champ.valeur_defaut })} champs={autres} rubriqueTarif={rubriqueTarif} /> : null}

      <ConfirmationIntegree libelle="Retirer ce champ" question={`Retirer le champ « ${champ.libelle} » du formulaire ? Les dossiers déjà déposés conservent leur donnée.`} libelleConfirmer="Retirer le champ" onConfirmer={onSupprimer} petit />
      <Bouton variante="discret" petit onClick={() => document.getElementById(`champ-item-${champ.code}`)?.focus()}>
        Revenir au champ dans le formulaire
      </Bouton>
    </div>
  );
}
